import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { environment } from '@env/environment';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { Page } from '@interfaces/page';
import { Observable, EMPTY, forkJoin } from 'rxjs';
import { defaultIfEmpty } from 'rxjs/operators';

export interface FormBannerDTO {
    id?:            number;
    file?:          File;
    filePath?:      string;
}

@Injectable({
  providedIn: 'root'
})
export class SiteBannerService {

  private readonly url = environment.apiBaseURL + "/form-banner";
  
  private readonly findIdURL            = this.url + '/{id}';
  private readonly pageableURL          = this.url + '/pageable';
  private readonly pageableCountURL     = this.url + '/pageable/count';
  private readonly uploadImageURL       = this.url + '/image/upload';

  constructor(private http: HttpClient) { }

  public pageableCountSite(sortingPaging: SortingPagingData, searchForm: any) {
     return forkJoin([
       this.getPagable(sortingPaging, searchForm),
       this.getCount(sortingPaging, searchForm)
     ]);
   }
  
  public getPagable(sortingPaging: any, searchObject: FormGroup): Observable<Page<FormBannerDTO>> {
    const requestParams = this.createSearchHttpRequestParams(sortingPaging, searchObject);
    return this.http.get<any>(this.pageableURL, { params: requestParams });
  }

  public getCount(sortingPaging: any, searchObject: FormGroup): Observable<number> {
    const requestParams = this.createSearchHttpRequestParams(sortingPaging, searchObject);
    return this.http.get<number>(`${this.pageableCountURL}`, { params: requestParams });
  }

  public findById(id: number): Observable<any> {
    if (!id) {
      return EMPTY.pipe(defaultIfEmpty());
    }
    return this.http.get<FormBannerDTO>(this.findIdURL.replace('{id}', `${id}`));
  }

  public save(banner: FormBannerDTO): Observable<number> {
    return this.http.post<number>(this.url, banner);
  }

  public update(id: number, banner: FormBannerDTO, file?: File): Observable<void> {
    const formData = new FormData();
    formData.append('formBannerDTO', JSON.stringify(banner));
    if (file) {
      formData.append('file', file);
    }
    return this.http.put<void>(this.findIdURL.replace('{id}', `${id}`), formData);
  }

  public uploadImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
  
    return this.http.post<string>(this.uploadImageURL, formData, { responseType: 'text' as 'json' });
  }

  private createSearchHttpRequestParams(sortingPaging: SortingPagingData, searchForm: FormGroup) {
    let requestParams = new HttpParams();

    if (sortingPaging != null) {
      requestParams = requestParams.set("page", sortingPaging.getPageNumber());
      requestParams = requestParams.set("pageSize", sortingPaging.getPageSize());
    }

    if (sortingPaging.isSortingValid()) {
      requestParams = requestParams
        .set("sortBy", sortingPaging.sortBy)
        .set("sortAsc", sortingPaging.sortAsc === null ? 'null' : (sortingPaging.sortAsc ? 'true' : 'false'));
    }

    Object.entries(searchForm).forEach(item => {
      if (item[1] && item[1].length !== 0) {
        requestParams = requestParams.set(item[0], item[1]);
      }
    });

    return requestParams;
  }
  
}
